import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IRatioResult } from '../../../data/providers/reports/ratioReportProvider';
import { ISize } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { legendFormatter } from '../../shared/graphs/legendFormatter';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { abbreviateMetricLabel } from '../utils/abbreviateMetricLabel';
import { Tooltip as RatioTooltip } from './Tooltip';
import { displayNamespacedLabel, namespaceLabel } from './utils';

const colors = ['#2196F3', '#ffd557'];

interface IProps {
  results: IRatioResult[];
  pdfMode: EPdfMode;
}

interface IDataPoint {
  [metricKey: string]: number | string;
  Date: number;
  metricOneUnits: string;
  metricTwoUnits: string;
}

interface IGraphData {
  labels: string[];
  data: IDataPoint[];
}

export const Graph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IGraphData => {
    const labels: string[] = [];
    if (props.results.length > 0) {
      const sample = props.results[0];
      labels.push(
        namespaceLabel(0, abbreviateMetricLabel(sample.metricOne.label)),
        namespaceLabel(1, abbreviateMetricLabel(sample.metricTwo.label))
      );
    }

    const mData = props.results.map((result: IRatioResult): IDataPoint => {
      return {
        Date: result.timestamp,
        [labels[0]]: result.metricOne.metric.value,
        [labels[1]]: result.metricTwo.metric.value,
        Ratio: result.ratio,
        metricOneUnits: result.metricOne.metric.units,
        metricTwoUnits: result.metricTwo.metric.units,
      };
    });

    const sortedData = sortBy(mData, 'Date', 'asc');

    return {
      labels,
      data: sortedData,
    };
  }, [JSON.stringify(props.results)]);

  const [maxValue, maxRatio] = React.useMemo((): number[] => {
    return props.results.reduce(
      (acc: number[], curr: IRatioResult): number[] => {
        const highValue =
          curr.metricOne.metric.value > curr.metricTwo.metric.value
            ? curr.metricOne.metric.value
            : curr.metricTwo.metric.value;
        const force = acc[0] > highValue ? acc[0] : highValue;
        const ratio = acc[1] > curr.ratio ? acc[1] : curr.ratio;
        return [force, ratio];
      },
      [0, 0]
    );
  }, [JSON.stringify(props.results)]);

  return (
    <Row>
      <Card
        noTopRadius
        inverse={shouldInvert(props.pdfMode)}
        style={{
          padding: '0px',
          minHeight: 'var(--graph-min-height)',
          ...(isPdf(props.pdfMode) ? { overflow: 'visible', height: 'auto' } : {}),
        }}
      >
        <Column>
          <GraphContainer
            inverse={shouldInvert(props.pdfMode)}
            style={{ paddingBlockEnd: '0px', ...(isPdf(props.pdfMode) ? { height: 'auto' } : {}) }}
          >
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <ComposedChart
                    width={size.width}
                    height={size.height}
                    data={data.data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      horizontal
                      stroke='#ccc'
                      strokeOpacity={0.25}
                    />

                    <XAxis
                      dataKey='Date'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                      tickFormatter={tickFormatter}
                    />

                    <YAxis
                      orientation='right'
                      yAxisId='right'
                      domain={[0, maxValue]}
                      axisLine={false}
                      tickLine={false}
                      tickCount={10}
                      tick={{ fill: 'var(--color-text)' }}
                    >
                      <Label
                        fill='var(--color-text)'
                        value={
                          props.results.length > 0
                            ? props.results?.[0]?.metricOne?.metric?.units
                            : null
                        }
                        position='right'
                        offset={5}
                        angle={90}
                      />
                    </YAxis>

                    <YAxis
                      yAxisId='left'
                      orientation='left'
                      dataKey={'Ratio'}
                      domain={[0, 2]}
                      tickCount={10}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                    >
                      <Label
                        fill='var(--color-text)'
                        value='Ratio'
                        position='left'
                        offset={-5}
                        angle={270}
                      />
                    </YAxis>

                    <Tooltip
                      cursor={false}
                      content={RatioTooltip}
                      formatter={tickFormatter}
                    />

                    {data.labels.map(
                      (m: string, i: number): React.ReactElement => {
                        return (
                          <Bar
                            key={i}
                            dataKey={m}
                            fill={colors[i]}
                            yAxisId='right'
                          />
                        );
                      }
                    )}

                    <Line
                      dataKey={'Ratio'}
                      yAxisId='left'
                      connectNulls
                      stroke={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-dark-text)'
                          : 'var(--color-text)'
                      }
                      strokeWidth={2}
                      color='var(--color-scatter-primary)'
                      dot={{
                        stroke: 'rgba(0,0,0, .85)',
                        strokeWidth: 2,
                        r: 6,
                        strokeDasharray: '',
                      }}
                      fill={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-scatter-primary-inverse)'
                          : 'var(--color-scatter-primary)'
                      }
                    />

                    <Legend
                      iconType='circle'
                      formatter={(value: string): React.ReactElement =>
                        legendFormatter(displayNamespacedLabel(value))
                      }
                    />
                  </ComposedChart>
                );
              }}
            </AutoSizer>
          </GraphContainer>
        </Column>
      </Card>
    </Row>
  );
};
const tickFormatter = (tick: number): string =>
  tick > 12875256000 ? dayjs.utc(Number(tick)).format('M/D') : tick.toString();
